import { mul, SRGB_TO_P3, P3_TO_SRGB, type Mat3 } from '../../color/matrix';
import { OK_M1, OK_M1_INV, OK_M2, OK_M2_INV } from '../../color/oklab';
import { BAND_CENTERS, HUE_BANDS } from '../../color/hsl';
// Shared GLSL snippets. Convention: texture t = 0 is the TOP image row; gl_FragCoord.y counts image rows
// from the top. Only the final present-to-canvas pass flips.

export const HEADER = `#version 300 es
precision highp float;
precision highp sampler2D;
precision highp sampler3D;
`;

export const COLOR_FNS = `
float srgbToLin(float c) { return c <= 0.04045 ? c / 12.92 : pow((c + 0.055) / 1.055, 2.4); }
float linToSrgb(float l) { return l <= 0.0031308 ? 12.92 * l : 1.055 * pow(l, 1.0 / 2.4) - 0.055; }
vec3 srgbToLin3(vec3 c) { return vec3(srgbToLin(c.r), srgbToLin(c.g), srgbToLin(c.b)); }
vec3 linToSrgb3(vec3 l) { return vec3(linToSrgb(l.r), linToSrgb(l.g), linToSrgb(l.b)); }
// Sign-preserving versions for out-of-gamut working values.
vec3 encodeSigned(vec3 l) { return sign(l) * linToSrgb3(abs(l)); }
vec3 decodeSigned(vec3 c) { return sign(c) * srgbToLin3(abs(c)); }
const vec3 LUMA_P3 = vec3(0.2289746, 0.6917385, 0.0792869);
float lumaP3(vec3 c) { return dot(c, LUMA_P3); }
`;

// ── OkLab / OkLCh in GLSL (§6.5). Constants generated from the same JS matrices the CPU uses. ──

const glMat = (m: Mat3) =>
  `mat3(${[m[0][0], m[1][0], m[2][0], m[0][1], m[1][1], m[2][1], m[0][2], m[1][2], m[2][2]].map((v) => v.toPrecision(10)).join(', ')})`;

export const OKLAB_FNS = `
const mat3 P3_TO_LMS = ${glMat(mul(OK_M1, P3_TO_SRGB))};
const mat3 LMS_TO_P3 = ${glMat(mul(SRGB_TO_P3, OK_M1_INV))};
const mat3 OK_M2 = ${glMat(OK_M2)};
const mat3 OK_M2_INV = ${glMat(OK_M2_INV)};
vec3 cbrt3(vec3 x) { return sign(x) * pow(abs(x), vec3(1.0 / 3.0)); }
vec3 p3ToOklab(vec3 c) { return OK_M2 * cbrt3(P3_TO_LMS * c); }
vec3 oklabToP3(vec3 lab) { vec3 l = OK_M2_INV * lab; return LMS_TO_P3 * (l * l * l); }
vec3 toLch(vec3 lab) {
  float C = length(lab.yz);
  float h = C < 1e-9 ? 0.0 : degrees(atan(lab.z, lab.y));
  return vec3(lab.x, C, h < 0.0 ? h + 360.0 : h);
}
vec3 fromLch(vec3 lch) { float r = radians(lch.z); return vec3(lch.x, lch.y * cos(r), lch.y * sin(r)); }
const float BAND_C[8] = float[8](${HUE_BANDS.map((b) => BAND_CENTERS[b].toFixed(1)).join(', ')});
float wrap180(float d) { return mod(d + 180.0, 360.0) - 180.0; }
float bandWeight(int i, float h) {
  float c = BAND_C[i];
  float d = wrap180(h - c);
  float span = d >= 0.0 ? wrap180(BAND_C[(i + 1) % 8] - c) : -wrap180(BAND_C[(i + 7) % 8] - c);
  if (span <= 0.0) span += 360.0;
  float t = abs(d) / span;
  return t < 1.0 ? 0.5 * (1.0 + cos(3.141592653589793 * t)) : 0.0;
}
// §6.5 gamut mapping: keep OkLab L and hue, reduce chroma until the colour fits the output gamut.
vec3 gamutOut(vec3 c, mat3 toOut) {
  vec3 o = toOut * c;
  if (all(greaterThanEqual(o, vec3(-1e-4))) && all(lessThanEqual(o, vec3(1.0 + 1e-4)))) return o;
  vec3 lch = toLch(p3ToOklab(c));
  float L = clamp(lch.x, 0.0, 1.0);
  float lo = 0.0, hi = 1.0;
  for (int i = 0; i < 12; i++) {
    float mid = 0.5 * (lo + hi);
    vec3 q = toOut * oklabToP3(fromLch(vec3(L, lch.y * mid, lch.z)));
    if (all(greaterThanEqual(q, vec3(-1e-4))) && all(lessThanEqual(q, vec3(1.0 + 1e-4)))) lo = mid; else hi = mid;
  }
  return toOut * oklabToP3(fromLch(vec3(L, lch.y * lo, lch.z)));
}
`;
